import { useMediaQuery, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar, ChartProps } from 'react-chartjs-2';
import { LoadingIndicator } from '../../loading-indicator/loading-indicator.component';
import { useBarCharStyles } from './bar-chart.styles';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

ChartJS.defaults.font.family = 'Poppins';

export interface BarChartProps {
  isLoading: boolean;
  data: ChartProps<'bar'>['data'];
  xAxisTickCallback?(value: string): string | undefined;
  tooltipTitleCallBack?(tooltipItems: TooltipItem<'bar'>): string | string[];
  hideTickLabelsOnMobile?: boolean;
  hideVerticalGridlinesOnMobile?: boolean;
}

export const BarChart = React.memo(function BarChart({
  isLoading,
  data,
  xAxisTickCallback,
  tooltipTitleCallBack,
  hideTickLabelsOnMobile,
  hideVerticalGridlinesOnMobile
}: BarChartProps) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useBarCharStyles();

  const options: ChartProps<'bar'>['options'] = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: tooltipTitleCallBack
              ? function (tooltipItems) {
                  return tooltipTitleCallBack(tooltipItems[0]);
                }
              : undefined
          }
        }
      },
      responsive: true,
      aspectRatio: isXS ? 2 : 3.5,
      scales: {
        x: {
          ticks: {
            align: 'center',
            maxRotation: 0,
            callback: xAxisTickCallback
              ? function (_value, index) {
                  if (isXS && hideVerticalGridlinesOnMobile) {
                    return undefined;
                  }
                  const trueValue = this.getLabelForValue(index);
                  const label = xAxisTickCallback(trueValue);
                  if (isXS && hideTickLabelsOnMobile && typeof label !== 'undefined') {
                    return '';
                  }
                  return label;
                }
              : undefined
          },
          grid: {
            offset: false
          }
        },
        y: {
          ticks: {
            maxTicksLimit: 5
          }
        }
      },
      elements: {
        bar: {
          borderRadius: 5
        }
      }
    }),
    [hideTickLabelsOnMobile, hideVerticalGridlinesOnMobile, isXS, tooltipTitleCallBack, xAxisTickCallback]
  );

  const hasData = useMemo(
    () => data.datasets.some((dataSet) => dataSet.data.some((data) => data > 0)),
    [data.datasets]
  );

  return (
    <div className={classes.wrapper}>
      <NoDataOverlay isLoading={isLoading} hasData={hasData} />
      <LoadingOverlay isLoading={isLoading} />
      <Bar options={options} data={data} />
    </div>
  );
});

function NoDataOverlay({ hasData, isLoading }: { isLoading: boolean; hasData: boolean }) {
  const classes = useBarCharStyles();
  if (hasData || isLoading) {
    return null;
  }
  return <span className={classes.noData}>No data...</span>;
}

function LoadingOverlay({ isLoading }: { isLoading: boolean }) {
  const classes = useBarCharStyles();
  if (!isLoading) {
    return null;
  }
  return (
    <div className={classes.loading}>
      <LoadingIndicator />
    </div>
  );
}
